export const howMarketsWork = {
    id: 'how-markets-work',
    title: 'Markets 101',
    summary: 'How prices and spreads work, buying and selling, the markets you can trade, their hours, trade sizes and why everything is priced in USDT.',
    level: 'beginner',
    art: 'btc',
    image: { src: '/academy/how-markets-work.webp', alt: 'A Bitcoin coin in front of a row of green and red price candles' },
    screen: { to: '/markets', label: 'Markets' },
    lessons: [
        {
            id: 'bid-ask-spread',
            title: 'Bid, ask and spread',
            minutes: 4,
            level: 'beginner',
            blocks: [
                { type: 'p', text: 'Every market has two prices. The **ask** is the price you buy at. The **bid** is the price you sell at. The ask is always a little above the bid.' },
                { type: 'p', text: 'The gap between them is the **spread**. The large price on the instrument screen is the **mark price**, halfway between bid and ask; the bid and ask are shown next to it.' },
                { type: 'example', title: 'What the spread costs', text: 'BTC shows bid $64,990 and ask $65,010, a $20 spread. You buy 0.1 BTC at the ask, $65,010. If you close straight away, you sell at the bid, $64,990: a loss of 0.1 × $20 = $2 before fees. The price has to move in your favour by the spread before the trade breaks even.' },
                { type: 'p', text: 'Spreads differ between markets and can widen when a market is fast or thin. A buy opens at the ask and closes at the bid; a sell does the opposite.' },
                { type: 'tip', text: 'Before you place an order, the order panel shows the estimated entry: the ask for a buy, the bid for a sell.' },
            ],
            quiz: [
                {
                    q: 'Bid $1.1000, ask $1.1002. You want to buy. Which price do you get?',
                    options: ['$1.1000', '$1.1001', '$1.1002'],
                    answer: 2,
                    why: 'Buys open at the ask, the higher of the two prices.',
                },
                {
                    q: 'You buy and close immediately, with no price change. What is the result?',
                    options: ['Zero', 'A small loss: the spread, plus fees', 'A small profit'],
                    answer: 1,
                    why: 'You bought at the ask and sold at the lower bid, so you lose the spread, and fees are charged on open and close.',
                },
            ],
            tryIt: { to: '/trade/BTC', label: 'See bid and ask on BTC' },
            terms: ['bid', 'ask', 'spread', 'mark-price'],
        },
        {
            id: 'buy-and-sell',
            title: 'Buy (long) and sell (short)',
            minutes: 5,
            level: 'beginner',
            blocks: [
                { type: 'p', text: 'You can trade in either direction. A **buy** is also called going **long**: you profit if the price rises. A **sell** is going **short**: you profit if the price falls. You don’t need to own anything to sell.' },
                {
                    type: 'compare',
                    left: { title: 'Buy (long)', items: ['Opens at the ask', 'Closes at the bid', 'Profits when the price rises', 'Take profit above entry, stop loss below'] },
                    right: { title: 'Sell (short)', items: ['Opens at the bid', 'Closes at the ask', 'Profits when the price falls', 'Take profit below entry, stop loss above'] },
                },
                { type: 'example', title: 'A short on gold', text: 'You sell 10 oz of gold at the bid, $2,400. Gold falls and you close at the ask, $2,380. Profit: 10 × $20 = $200. If gold had risen to $2,420 instead, you would have lost 10 × $20 = $200.' },
                { type: 'p', text: 'Fees and swap apply to both directions. Swap can differ for buys and sells; the instrument screen shows both rates.' },
                { type: 'warn', text: 'A rising price has no ceiling, so the loss on a short has no fixed limit. Use a stop loss.' },
            ],
            quiz: [
                {
                    q: 'You think oil will fall. Which order do you place?',
                    options: ['Buy', 'Sell', 'Neither: you can only profit when prices rise'],
                    answer: 1,
                    why: 'A sell (short) profits when the price falls.',
                },
                {
                    q: 'At which price does a short trade close?',
                    options: ['The bid', 'The mark price', 'The ask'],
                    answer: 2,
                    why: 'A short opens at the bid and closes at the ask, the reverse of a buy.',
                },
                {
                    q: 'Where does a stop loss go on a short trade?',
                    options: ['Above the entry price', 'Below the entry price', 'At the entry price'],
                    answer: 0,
                    why: 'A short loses when the price rises, so the stop loss sits above the entry.',
                },
            ],
            tryIt: { to: '/trade/BTC', label: 'Try Buy and Sell in the order panel' },
            practice: { id: 'practice-short', text: 'Open a sell (short) trade on your demo account.', check: 'demo_short' },
            terms: ['long', 'short', 'swap'],
        },
        {
            id: 'asset-classes',
            title: 'Crypto, forex, commodities, stocks, indices',
            minutes: 4,
            level: 'beginner',
            blocks: [
                { type: 'p', text: 'Markets are grouped into five **asset classes**. On the Markets screen you can filter by each one, search, and keep favourites.' },
                {
                    type: 'list',
                    items: [
                        '**Crypto**: Bitcoin, Ethereum and other coins. Open every day, around the clock.',
                        '**Forex**: currency pairs such as EUR/USD and USD/JPY.',
                        '**Commodities**: for example gold, silver, oil and natural gas.',
                        '**Stocks**: shares of companies such as Apple and Microsoft.',
                        '**Indices**: a basket of shares in one price, such as the US 500 or Germany 40.',
                    ],
                },
                { type: 'p', text: 'The asset class decides two things that affect you: the **trading hours**, and the **swap days**. Crypto is charged swap every day at {swapHour}. The other classes skip weekends and are charged three times on {swapTripleDay} instead.' },
                { type: 'p', text: 'Each market also has its own spread, fee, leverage range and trade size limits. The order panel shows them for the market you have open.' },
                { type: 'tip', text: 'Prices move differently in each class. Start small in a market you have not traded before.' },
            ],
            quiz: [
                {
                    q: 'Which asset class is open on Sundays?',
                    options: ['Stocks', 'Crypto', 'Indices'],
                    answer: 1,
                    why: 'Crypto trades every day. The other classes close at the weekend.',
                },
                {
                    q: 'On which day are non-crypto trades charged swap three times?',
                    options: ['{swapTripleDay}', 'Friday', 'Every day'],
                    answer: 0,
                    why: 'Non-crypto markets skip swap at weekends and are charged ×3 on {swapTripleDay} instead.',
                },
            ],
            tryIt: { to: '/markets', label: 'Filter the markets by class' },
            terms: ['asset-class', 'swap'],
        },
        {
            id: 'trading-hours-weekends',
            title: 'Trading hours and weekends',
            minutes: 4,
            level: 'beginner',
            blocks: [
                { type: 'p', text: 'Crypto is open 24 hours a day, every day. Other markets follow their own **trading hours**: forex runs from Sunday evening to Friday evening (New York time), US stocks trade in the US session on weekdays, and some markets pause for a short break each day.' },
                { type: 'p', text: 'The instrument screen shows whether a market is open. On a phone it says **Open 24/7**, **Open**, or when it **Opens** next. On a computer, a closed market shows **Market closed**.' },
                {
                    type: 'list',
                    items: [
                        'While a market is closed you cannot open new trades or close trades in it.',
                        'Open trades stay open over the weekend and keep their margin.',
                        'Pending orders, take profits and stop losses wait for the next price after the market reopens.',
                    ],
                },
                { type: 'example', title: 'A weekend gap', text: 'You hold a buy on the US 500 with a stop loss at 5,800. It closes on Friday at 5,850 and opens on Monday at 5,750. Your stop loss closes the trade at the first price after the open, about 5,750, not at 5,800.' },
                { type: 'warn', text: 'Prices can jump between the close and the next open. A stop loss then fills at the next available price, which can be worse than the level you set.' },
            ],
            quiz: [
                {
                    q: 'Gold is closed for the weekend. Can you close your gold trade on Saturday?',
                    options: ['Yes, at Friday’s price', 'No, you can close it when the market reopens', 'Yes, but only half of it'],
                    answer: 1,
                    why: 'Trades in a closed market can’t be opened or closed until it reopens.',
                },
                {
                    q: 'Your stop loss is at $100. The market reopens at $95. Where does the trade close?',
                    options: ['Exactly $100', 'Around $95, the first available price', 'It stays open'],
                    answer: 1,
                    why: 'After a gap, a stop loss fills at the next available price, which can be worse than your level.',
                },
            ],
            tryIt: { to: '/markets', label: 'Check which markets are open' },
            terms: ['trading-hours', 'stop-loss'],
        },
        {
            id: 'lots-units-usd',
            title: 'Lots, units and USDT size',
            minutes: 5,
            level: 'beginner',
            blocks: [
                { type: 'p', text: 'You can size an order in two ways. Switch with **Order size in: USDT / Lots** in the order panel.' },
                {
                    type: 'compare',
                    left: { title: 'USDT', items: ['Type the value of the trade in USDT', 'The panel shows the units and lots it equals', 'A slider sizes it as a % of your maximum'] },
                    right: { title: 'Lots', items: ['Type a volume in lots', 'Units = lots × lot size', 'The panel shows the notional in USDT'] },
                },
                { type: 'p', text: 'The **lot size** is how many units one lot holds. It is set per market and shown as **Contract size** in the order panel, with the minimum volume and the step.' },
                { type: 'example', title: 'Lots into dollars', text: 'For gold, 1 lot = 100 oz. At $2,400 an ounce, 0.01 lots is 1 oz, worth $2,400. For EUR/USD, 1 lot = 100,000 euros, so 0.01 lots at 1.1000 is 1,000 euros, worth $1,100.' },
                { type: 'p', text: 'Either way, the trade is the same: a quantity of units at a price. The panel also shows **Value of $1 move**, how much your profit or loss changes for each $1 the price moves.' },
                { type: 'tip', text: 'One lot can be a large trade. Check the notional before you place an order in lots.' },
            ],
            quiz: [
                {
                    q: 'Gold: 1 lot = 100 oz. How many ounces is 0.05 lots?',
                    options: ['0.05 oz', '5 oz', '50 oz'],
                    answer: 1,
                    why: 'Units = lots × lot size = 0.05 × 100 = 5 oz.',
                },
                {
                    q: 'You type $1,000 for BTC at $50,000. How much BTC is that?',
                    options: ['0.02 BTC', '0.2 BTC', '2 BTC'],
                    answer: 0,
                    why: '$1,000 ÷ $50,000 = 0.02 BTC.',
                },
            ],
            tryIt: { to: '/trade/BTC', label: 'Switch between USDT and lots' },
            terms: ['lot', 'lot-size', 'notional'],
        },
        {
            id: 'prices-in-usdt',
            title: 'Why prices are in USDT',
            minutes: 4,
            level: 'beginner',
            blocks: [
                { type: 'p', text: 'Your account is in **USDT**, a stablecoin built to stay worth $1, so markets are shown against it: BTC/USDT, GOLD/USDT and so on, with prices in $. Profit, loss, margin and fees are then in the same currency as your balance. Currency pairs keep their real names, such as EUR/USD.' },
                {
                    type: 'list',
                    items: [
                        '**Crypto** prices come from the global USDT markets.',
                        '**Indices** quoted in another currency (Germany 40 and France 40 in euros, UK 100 in pounds, Hong Kong 50 in Hong Kong dollars, Australia 200 in Australian dollars) are converted to dollars at the live exchange rate. Their prices are in $, not local index points.',
                        '**Currency pairs** keep their market price: USD/JPY is in yen, EUR/GBP in pounds. Notional, margin, fees, swap and profit or loss are converted to USDT at the live rate.',
                    ],
                },
                { type: 'example', title: 'Profit on USD/JPY', text: 'You buy 1,000 USD/JPY at 150.00 and close at 151.00. The profit is 1,000 × 1.00 = 1,000 yen. At 151 yen to the dollar that is about $6.62, added to your balance in USDT.' },
                { type: 'p', text: 'On pairs like this, the profit or loss is converted at the rate when you close the trade.' },
            ],
            quiz: [
                {
                    q: 'The Germany 40 index is quoted in euros on its exchange. How is its price shown here?',
                    options: ['In euros', 'In $, converted at the live rate', 'In index points with no currency'],
                    answer: 1,
                    why: 'Indices quoted in another currency are converted to dollars at the live exchange rate.',
                },
                {
                    q: 'You close a profitable EUR/GBP trade. In which currency is the profit added to your balance?',
                    options: ['Pounds', 'Euros', 'USDT, your account currency'],
                    answer: 2,
                    why: 'The pair is priced in pounds, but profit and loss are converted to USDT, the currency of your account.',
                },
            ],
            tryIt: { to: '/markets', label: 'See the prices' },
            terms: ['pnl', 'notional'],
        },
    ],
};
export const howMarketsWorkTerms = [
    { id: 'bid', term: 'Bid', text: 'The lower of the two prices: where you sell. Buys close at the bid.', lesson: 'bid-ask-spread' },
    { id: 'ask', term: 'Ask', text: 'The higher of the two prices: where you buy. Sells close at the ask.', lesson: 'bid-ask-spread' },
    { id: 'spread', term: 'Spread', text: 'The gap between the ask and the bid. A trade starts that far from break-even, before fees.', lesson: 'bid-ask-spread' },
    { id: 'mark-price', term: 'Mark price', text: 'The price halfway between the bid and the ask, shown as the main price of a market. Yield also converts your money to a coin and back at the mark price.', lesson: 'bid-ask-spread' },
    { id: 'long', term: 'Long (buy)', text: 'A trade that profits when the price rises. Opens at the ask and closes at the bid.', lesson: 'buy-and-sell' },
    { id: 'short', term: 'Short (sell)', text: 'A trade that profits when the price falls. Opens at the bid and closes at the ask.', lesson: 'buy-and-sell' },
    { id: 'asset-class', term: 'Asset class', text: 'A group of markets: crypto, forex, commodities, stocks or indices. It decides the trading hours and the swap days.', lesson: 'asset-classes' },
    { id: 'trading-hours', term: 'Trading hours', text: 'When a market can be traded. Crypto is open 24/7; other markets close at weekends and some pause daily. A closed market can’t be traded until it reopens.', lesson: 'trading-hours-weekends' },
    { id: 'lot', term: 'Lot', text: 'A standard trade size. Units = lots × the market’s lot size; for example 0.01 lots of gold is 1 oz.', lesson: 'lots-units-usd' },
    { id: 'lot-size', term: 'Lot size (contract size)', text: 'How many units one lot holds in a market, e.g. 100 oz for gold. Shown as Contract size in the order panel.', lesson: 'lots-units-usd' },
];
//# sourceMappingURL=how-markets-work.js.map